/**
 * 3D 图标按钮 + 模态框查看器
 * 使用 ES6 模块，需要 Shopify 主题支持
 */

// Three.js Import Map
const importMap = document.createElement('script');
importMap.type = 'importmap';
importMap.textContent = JSON.stringify({
  imports: {
    three: 'https://unpkg.com/three@0.160.0/build/three.module.js',
    'three/addons/': 'https://unpkg.com/three@0.160.0/examples/jsm/'
  }
});
if (!document.querySelector('script[type="importmap"]')) {
  document.head.prepend(importMap);
}

/**
 * 3D 模型查看器类
 */
class Product3DModalViewer {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.modelUrl = null;
    this.modelViewer = null;

    // DOM 元素
    this.view3dBtn = document.getElementById(`view3dBtn-${sectionId}`);
    this.viewerModal = document.getElementById(`viewerModal-${sectionId}`);
    this.closeBtn = document.getElementById(`closeBtn-${sectionId}`);
    this.modalContent = document.getElementById(`modalContent-${sectionId}`);
    this.modalLoading = document.getElementById(`modalLoading-${sectionId}`);
    this.loadingProgress = document.getElementById(`loadingProgress-${sectionId}`);
    this.modalError = document.getElementById(`modalError-${sectionId}`);

    // 获取模型 URL
    const buttonWrapper = this.view3dBtn?.closest('[data-model-url]');
    if (buttonWrapper) {
      this.modelUrl = buttonWrapper.dataset.modelUrl;
    }

    this.init();
  }

  async init() {
    // 绑定事件
    this.bindEvents();

    // 设置实例特定的回调函数（支持多实例）
    this.instanceCallbacks = {
      updateLoadingProgress: this.updateLoadingProgress.bind(this),
      hideLoading: this.hideLoading.bind(this),
      showError: this.showError.bind(this)
    };

    // 将回调函数存储到实例上，供 ModelViewer 使用
    this.view3dBtn._3dCallbacks = this.instanceCallbacks;
  }

  bindEvents() {
    if (!this.view3dBtn) return;

    // 点击按钮打开模态框
    this.view3dBtn.addEventListener('click', () => this.openModal());

    // 点击关闭按钮
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.closeModal());
    }

    // 点击背景关闭
    if (this.viewerModal) {
      this.viewerModal.addEventListener('click', (e) => {
        if (e.target === this.viewerModal) {
          this.closeModal();
        }
      });
    }

    // ESC 键关闭
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.viewerModal?.classList.contains('active')) {
        this.closeModal();
      }
    });

    // Shopify 主题编辑器事件
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', (e) => {
        if (e.detail.sectionId === this.sectionId) {
          this.reload();
        }
      });

      document.addEventListener('shopify:section:unload', (e) => {
        if (e.detail.sectionId === this.sectionId) {
          this.dispose();
        }
      });
    }
  }

  async openModal() {
    if (!this.modelUrl) {
      this.showError();
      this.viewerModal?.classList.add('active');
      return;
    }

    this.showLoading();
    this.viewerModal?.classList.add('active');
    document.body.style.overflow = 'hidden';

    // 动态导入 Three.js 并初始化
    if (!this.modelViewer) {
      try {
        const THREE = await import('three');
        const { OrbitControls } = await import('three/addons/controls/OrbitControls.js');
        const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');

        this.modelViewer = new ModelViewer(this.modalContent, {
          modelUrl: this.modelUrl,
          backgroundColor: 0xffffff,
          ambientLightIntensity: 2,
          exposure: 1.5,
          THREE,
          OrbitControls,
          GLTFLoader,
          callbacks: this.instanceCallbacks
        });
      } catch (error) {
        console.error('Failed to load Three.js:', error);
        this.showError();
      }
    }
  }

  closeModal() {
    this.viewerModal?.classList.remove('active');
    document.body.style.overflow = '';

    // 延迟清理资源
    setTimeout(() => {
      if (this.modelViewer) {
        this.modelViewer.dispose();
        this.modelViewer = null;
      }
      this.hideLoading();
      this.modalError?.classList.remove('show');
    }, 300);
  }

  showLoading() {
    this.modalLoading?.classList.remove('hidden');
    this.modalError?.classList.remove('show');
  }

  hideLoading() {
    this.modalLoading?.classList.add('hidden');
  }

  updateLoadingProgress(progress) {
    if (this.loadingProgress) {
      this.loadingProgress.textContent = `${Math.round(progress)}%`;
    }
  }

  showError() {
    this.hideLoading();
    this.modalError?.classList.add('show');
  }

  reload() {
    // Section 重新加载时的处理
    this.dispose();
    this.init();
  }

  dispose() {
    this.closeModal();
  }
}

/**
 * 3D 模型查看器核心类
 */
class ModelViewer {
  constructor(container, options) {
    this.container = container;
    this.config = {
      modelUrl: options.modelUrl || '',
      backgroundColor: options.backgroundColor || 0x1a1a1a,
      ambientLightIntensity: options.ambientLightIntensity ?? 2,
      exposure: options.exposure ?? 1.5,
      cameraFov: options.cameraFov || 45,
      minDistance: options.minDistance || 0.4,
      maxDistance: options.maxDistance || 10,
    };

    this.THREE = options.THREE;
    this.OrbitControls = options.OrbitControls;
    this.GLTFLoader = options.GLTFLoader;
    this.callbacks = options.callbacks || null;

    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.model = null;
    this.animationId = null;

    this._init();
  }

  async _init() {
    this._initScene();
    this._initCamera();
    this._initRenderer();
    this._initLights();
    this._initControls();
    this._bindResizeEvent();

    if (this.config.modelUrl) {
      await this._loadModel();
    }

    this._animate();
  }

  _initScene() {
    this.scene = new this.THREE.Scene();
    this.scene.background = new this.THREE.Color(this.config.backgroundColor);
  }

  _initCamera() {
    const rect = this.container.getBoundingClientRect();
    const aspect = rect.width / rect.height;
    this.camera = new this.THREE.PerspectiveCamera(this.config.cameraFov, aspect, 0.1, 100);
    this.camera.position.set(3, 2, 3);
  }

  _initRenderer() {
    this.renderer = new this.THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const rect = this.container.getBoundingClientRect();
    this.renderer.setSize(rect.width, rect.height);

    this.renderer.toneMapping = this.THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = this.config.exposure;

    this.container.appendChild(this.renderer.domElement);
  }

  _initLights() {
    const ambientLight = new this.THREE.AmbientLight(0xffffff, this.config.ambientLightIntensity);
    this.scene.add(ambientLight);

    const directionalLight = new this.THREE.DirectionalLight(0xffffff, 2);
    directionalLight.position.set(5, 10, 7);
    this.scene.add(directionalLight);

    const fillLight = new this.THREE.DirectionalLight(0xffffff, 2);
    fillLight.position.set(-5, 5, -5);
    this.scene.add(fillLight);
  }

  _initControls() {
    this.controls = new this.OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.rotateSpeed = 1.5;
    this.controls.minDistance = this.config.minDistance;
    this.controls.maxDistance = this.config.maxDistance;
    this.controls.target.set(0, 0, 0);
  }

  async _loadModel() {
    if (!this.config.modelUrl) {
      console.error('No model URL provided');
      return;
    }

    try {
      const loader = new this.GLTFLoader();
      const gltf = await new Promise((resolve, reject) => {
        loader.load(
          this.config.modelUrl,
          resolve,
          (xhr) => {
            if (xhr.lengthComputable) {
              // 确保 progress 不超过 100%
              let progress = (xhr.loaded / xhr.total) * 100;
              progress = Math.min(progress, 100);
              this._onLoadProgress(progress);
            }
          },
          reject
        );
      });

      this.model = gltf.scene;
      this._fitCameraToModel();
      this.scene.add(this.model);
      this._onLoadComplete();
    } catch (error) {
      console.error('Model loading failed:', error);
      this._onLoadError(error);
    }
  }

  _fitCameraToModel() {
    const box = new this.THREE.Box3().setFromObject(this.model);
    const center = box.getCenter(new this.THREE.Vector3());
    const size = box.getSize(new this.THREE.Vector3());
    this.model.position.sub(center);

    const maxDim = Math.max(size.x, size.y, size.z);
    const distance = maxDim * 1.5;
    this.camera.position.set(distance, distance * 0.5, distance);
    this.controls.update();
  }

  _onLoadProgress(progress) {
    if (this.callbacks && this.callbacks.updateLoadingProgress) {
      this.callbacks.updateLoadingProgress(progress);
    }
  }

  _onLoadComplete() {
    if (this.callbacks && this.callbacks.hideLoading) {
      this.callbacks.hideLoading();
    }
  }

  _onLoadError(error) {
    if (this.callbacks && this.callbacks.showError) {
      this.callbacks.showError();
    }
  }

  _bindResizeEvent() {
    const resizeObserver = new ResizeObserver(() => {
      const rect = this.container.getBoundingClientRect();
      this.camera.aspect = rect.width / rect.height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(rect.width, rect.height);
    });
    resizeObserver.observe(this.container);
  }

  _animate() {
    this.animationId = requestAnimationFrame(() => this._animate());
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  dispose() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    if (this.renderer) {
      this.renderer.dispose();
      if (this.container.contains(this.renderer.domElement)) {
        this.container.removeChild(this.renderer.domElement);
      }
    }
    if (this.model) {
      this.model.traverse((node) => {
        if (node.isMesh) {
          if (node.geometry) node.geometry.dispose();
          if (node.material) {
            if (Array.isArray(node.material)) {
              node.material.forEach(m => m.dispose());
            } else {
              node.material.dispose();
            }
          }
        }
      });
    }
  }
}

// 初始化所有 3D 按钮查看器
function initProduct3DViewers() {
  const wrappers = document.querySelectorAll('[data-section-id]');

  wrappers.forEach(wrapper => {
    const sectionId = wrapper.dataset.sectionId;
    const view3dBtn = document.getElementById(`view3dBtn-${sectionId}`);

    if (!view3dBtn || view3dBtn.dataset.initialized) return;

    // 创建查看器实例
    const viewer = new Product3DModalViewer(sectionId);
    view3dBtn.dataset.initialized = 'true';
  });
}

// DOM 加载完成后初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProduct3DViewers);
} else {
  initProduct3DViewers();
}

// 导出类供外部使用
if (window.Shopify) {
  window.Product3DModalViewer = Product3DModalViewer;
  window.ModelViewer = ModelViewer;
}
